import { Search, X } from 'lucide-react';
import { useEffect, useState, useCallback } from 'react';

interface DebouncedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
    searchValue?: string;
    onChange?: (value: string) => void;
    searchFn?: (value: string) => void;
    debounce?: number;
    placeholder?: string;
    loading?: boolean;
}

const DebouncedInput = ({
    searchValue = '',
    onChange,
    debounce = 500,
    placeholder = 'Search...',
    searchFn,
    loading = false,
    ...props
}: DebouncedInputProps) => {
    const [value, setValue] = useState<string>(searchValue);
    const [isDebouncing, setIsDebouncing] = useState<boolean>(false);

    const debouncedSearch = useCallback(
        (searchValue: string) => {
            if (searchFn) {
                setIsDebouncing(true);
                searchFn(searchValue);
            }
        },
        [searchFn]
    );

    useEffect(() => {
        const timeout = setTimeout(() => {
            debouncedSearch(value);
            setIsDebouncing(false);
        }, debounce);
        return () => clearTimeout(timeout);
    }, [value, debounce, debouncedSearch]);

    const handleClear = () => {
        setValue('');
        onChange?.('');
        searchFn?.('');
    };

    return (
        <div className="relative flex-1 flex items-center gap-2">
            <input
                {...props}
                // type="search"
                role="searchbox"
                aria-label="Search input"
                placeholder={placeholder}
                value={value}
                onChange={(e) => {
                    setValue(e.target.value);
                    onChange?.(e.target.value);
                }}
                className="ltr:pl-10 rtl:pr-10 search form-input border-slate-200
                    focus:outline-none focus:border-custom-500 disabled:bg-slate-100
                    disabled:border-slate-300 disabled:text-slate-500
                    placeholder:text-slate-400 w-full transition-all duration-200"
            />
            <Search
                className={`inline-block size-4 absolute ltr:left-3 rtl:right-3
                    top-1/2 transform -translate-y-1/2 text-slate-500
                    ${isDebouncing || loading ? 'animate-spin' : ''}`}
            />
            {value && (
                <button
                    type="button"
                    onClick={handleClear}
                    className="absolute ltr:right-3 rtl:left-3 top-1/2 transform
                        -translate-y-1/2 p-1 hover:bg-slate-100 rounded-full transition-colors"
                    aria-label="Clear search"
                >
                    <X className="size-4 text-slate-500" />
                </button>
            )}
        </div>
    );
};
export default DebouncedInput;
